import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Logo from "../../assets/images/Logo.png";
import expertiseImage from "../../assets/images/home/expertise.png";

import home1 from "../../assets/images/home/home1.jpg";
import home2 from "../../assets/images/home/home2.jpg";
import home3 from "../../assets/images/home/home3.jpg";
import home4 from "../../assets/images/home/home4.jpg";
import home5 from "../../assets/images/home/home5.jpg";
import home6 from "../../assets/images/home/home6.jpg";
import home7 from "../../assets/images/home/home7.jpg";
import home8 from "../../assets/images/home/home8.jpg";

import { blogPosts } from "../../data/blogData";

import gensler from "../../assets/images/firm/Gensler/image5.jpeg";
import foster from "../../assets/images/firm/foster/foster.jpeg";
import arcadis from "../../assets/images/firm/arcadis/arcadis.jpeg";
import nikken from "../../assets/images/firm/niken/niken.jpeg";
import aecom from "../../assets/images/firm/aecom/aecom.jpeg";
import perkins from "../../assets/images/firm/perkins/perkins.jpeg";
import hdr from "../../assets/images/firm/hdr/hdr.jpeg";
import Skidmore from "../../assets/images/firm/skidmore/skidmore.jpeg";

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

// Add dummy images for firms
const firmImages = {
  Gensler: gensler,
  Arcadis: arcadis,
  "HDR (Henningson, Durham & Richardson)": hdr,
  "Nikken Sekkei": nikken,
  AECOM: aecom,
  "Perkins + Will": perkins,
  "Foster + Partners": foster,
  "Skidmore, Owings & Merrill (SOM)": Skidmore,
};

// Add firm links for each firm
const firmLinks = {
  Gensler: "/firms/gensler",
  Arcadis: "/firms/arcadis",
  "HDR (Henningson, Durham & Richardson)": "/firms/hdr",
  "Nikken Sekkei": "/firms/nikken-sekkei",
  AECOM: "/firms/aecom",
  "Perkins + Will": "/firms/perkins-will",
  "Skidmore, Owings & Merrill (SOM)": "/firms/som",
  "Foster + Partners": "/firms/foster-partners",
};

const HomePage = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [hoveredFirm, setHoveredFirm] = useState(null);
  const [selectedFirm, setSelectedFirm] = useState(null);
  const slides = [home1, home2, home3];

  // Refs for GSAP animations
  const headerRef = useRef(null);
  const textRef = useRef(null);
  const navigationRef = useRef(null);
  const overlayRef = useRef(null);
  const slideRefs = useRef([]);

  // Title section refs
  const titleLogoRef = useRef(null);
  const titleHeadingRef = useRef(null);
  const titleTextRef = useRef(null);

  // Expertise section refs
  const expertiseTitleRef = useRef(null);
  const expertiseLineRef = useRef(null);
  const expertiseListRef = useRef(null);
  const expertiseImageRef = useRef(null);
  const firmRefs = useRef([]);

  // Projects section refs
  const projectsTitleRef = useRef(null);
  const projectsDescRef = useRef(null);
  const projectsButtonRef = useRef(null);

  // Related expertise section refs
  const relatedExpertiseTitleRef = useRef(null);
  const relatedExpertiseHeadingRef = useRef(null);
  const relatedExpertiseTextRef = useRef(null);
  const relatedExpertiseButtonRef = useRef(null);

  // News section refs
  const newsRef = useRef(null);
  const newsCardRefs = useRef([]);

  // Add these refs for parallax
  const heroParallaxRef = useRef(null);
  const projectsParallaxRef = useRef(null);
  const relatedExpertiseParallaxRef = useRef(null);
  const workImageRefs = useRef([]);

  const navigate = useNavigate();

  // Initial animations
  useEffect(() => {
    // Initial fade in animation for the overlay
    gsap.fromTo(
      overlayRef.current,
      { opacity: 0 },
      { opacity: 1, duration: 1, ease: "power2.inOut" }
    );

    // Stagger animations for content
    const tl = gsap.timeline({ defaults: { ease: "power3.out" } });

    tl.fromTo(
      headerRef.current,
      { y: 100, opacity: 0 },
      { y: 0, opacity: 1, duration: 1 },
      0.5
    )
      .fromTo(
        textRef.current,
        { y: 50, opacity: 0 },
        { y: 0, opacity: 1, duration: 1 },
        "-=0.5"
      )
      .fromTo(
        navigationRef.current,
        { x: 50, opacity: 0 },
        { x: 0, opacity: 1, duration: 1 },
        "-=0.5"
      );
  }, []);

  // Title section animations
  useEffect(() => {
    const tl = gsap.timeline({
      defaults: { ease: "power3.out" },
    });

    tl.fromTo(
      titleLogoRef.current,
      { y: 50, opacity: 0 },
      { y: 0, opacity: 1, duration: 1 }
    )
      .fromTo(
        titleHeadingRef.current,
        { y: 50, opacity: 0 },
        { y: 0, opacity: 1, duration: 1 },
        "-=0.5"
      )
      .fromTo(
        titleTextRef.current,
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, duration: 1 },
        "-=0.5"
      );
  }, []);

  // Expertise section animations
  useEffect(() => {
    // Title and line animation
    gsap.fromTo(
      [expertiseTitleRef.current, expertiseLineRef.current],
      {
        x: -50,
        opacity: 0,
      },
      {
        x: 0,
        opacity: 1,
        duration: 1,
        ease: "power3.out",
        scrollTrigger: {
          trigger: expertiseTitleRef.current,
          start: "top 80%",
        },
      }
    );

    // Stagger animation for firm list
    gsap.fromTo(
      firmRefs.current,
      {
        y: 30,
        opacity: 0,
      },
      {
        y: 0,
        opacity: 1,
        duration: 0.8,
        stagger: 0.1,
        ease: "power3.out",
        scrollTrigger: {
          trigger: expertiseListRef.current,
          start: "top 80%",
        },
      }
    );

    // Image animation
    gsap.fromTo(
      expertiseImageRef.current,
      {
        x: 50,
        opacity: 0,
      },
      {
        x: 0,
        opacity: 1,
        duration: 1.2,
        ease: "power3.out",
        scrollTrigger: {
          trigger: expertiseImageRef.current,
          start: "top 80%",
        },
      }
    );
  }, []);

  // Projects section animations
  useEffect(() => {
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: projectsTitleRef.current,
        start: "top 80%",
      },
    });

    tl.fromTo(
      projectsTitleRef.current,
      { y: 50, opacity: 0 },
      { y: 0, opacity: 1, duration: 1 }
    )
      .fromTo(
        projectsDescRef.current,
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8 },
        "-=0.5"
      )
      .fromTo(
        projectsButtonRef.current,
        { y: 20, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.6 },
        "-=0.3"
      );
  }, []);

  // Related expertise section animations
  useEffect(() => {
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: relatedExpertiseTitleRef.current,
        start: "top 80%",
      },
    });

    tl.fromTo(
      [relatedExpertiseTitleRef.current],
      { y: 30, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.8 }
    )
      .fromTo(
        relatedExpertiseHeadingRef.current,
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8 },
        "-=0.4"
      )
      .fromTo(
        relatedExpertiseTextRef.current,
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8 },
        "-=0.4"
      )
      .fromTo(
        relatedExpertiseButtonRef.current,
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8 },
        "-=0.4"
      );
  }, []);

  // News section animations
  useEffect(() => {
    gsap.fromTo(
      newsCardRefs.current,
      {
        y: 30,
        opacity: 0,
      },
      {
        y: 0,
        opacity: 1,
        duration: 0.8,
        stagger: 0.1,
        ease: "power3.out",
        scrollTrigger: {
          trigger: newsRef.current,
          start: "top 80%",
        },
      }
    );
  }, []);

  // Add parallax effects
  useEffect(() => {
    // Hero section parallax
    gsap.set(heroParallaxRef.current, { scale: 1.1 });
    gsap.to(heroParallaxRef.current, {
      yPercent: 15,
      ease: "none",
      scrollTrigger: {
        trigger: heroParallaxRef.current,
        start: "top top",
        end: "bottom top",
        scrub: 1,
      },
    });

    // Projects section parallax
    gsap.to(projectsParallaxRef.current, {
      yPercent: 15,
      scale: 1.1,
      ease: "none",
      scrollTrigger: {
        trigger: projectsParallaxRef.current,
        start: "top bottom",
        end: "bottom top",
        scrub: 1,
      },
    });

    // Related expertise section parallax
    gsap.to(relatedExpertiseParallaxRef.current, {
      yPercent: 15,
      scale: 1.1,
      ease: "none",
      scrollTrigger: {
        trigger: relatedExpertiseParallaxRef.current,
        start: "top bottom",
        end: "bottom top",
        scrub: 1,
      },
    });

    // Work images parallax
    workImageRefs.current.forEach((ref) => {
      gsap.to(ref, {
        yPercent: 10,
        scale: 1.1,
        ease: "none",
        scrollTrigger: {
          trigger: ref,
          start: "top bottom",
          end: "bottom top",
          scrub: 1,
        },
      });
    });

    // Helper function for parallax effect
    const createParallax = (element) => {
      if (!element) return;
      gsap.to(element, {
        yPercent: 20,
        ease: "none",
        scrollTrigger: {
          trigger: element.parentElement,
          start: "top bottom",
          end: "bottom top",
          scrub: true,
        },
      });
    };

    // Apply parallax to all images
    if (window.innerWidth >= 1024) {
      const images = document.querySelectorAll("img");
      images.forEach((img) => {
        img.style.transform = "scale(1.1)";
        createParallax(img);
      });
    }
  }, []);

  // Slide change animation
  const animateSlideChange = useCallback(
    (newIndex) => {
      // Crossfade animation
      gsap.to(slideRefs.current[currentSlide], {
        opacity: 0,
        duration: 1.2,
        ease: "power2.inOut",
      });

      gsap.fromTo(
        slideRefs.current[newIndex],
        { opacity: 0 },
        {
          opacity: 1,
          duration: 1.2,
          ease: "power2.inOut",
        }
      );

      setCurrentSlide(newIndex);
    },
    [currentSlide]
  );

  useEffect(() => {
    const timer = setInterval(() => {
      const nextSlide = (currentSlide + 1) % slides.length;
      animateSlideChange(nextSlide);
    }, 5000);

    return () => clearInterval(timer);
  }, [currentSlide, animateSlideChange, slides.length]);

  // Handle firm link hover animations
  const handleFirmHoverEnter = (e, firm) => {
    // Only apply hover effects on desktop
    if (window.innerWidth >= 1024) {
      const link = e.currentTarget;
      const line = link.querySelector(".line");

      setHoveredFirm(firm);

      gsap.to(link, {
        x: 20,
        color: "#38bdf8", // sky-400 color
        duration: 0.3,
        ease: "power2.out",
      });

      gsap.to(line, {
        width: "50%",
        duration: 0.3,
        ease: "power2.out",
      });
    }
  };

  const handleFirmHoverLeave = (e) => {
    // Only apply hover effects on desktop
    if (window.innerWidth >= 1024) {
      const link = e.currentTarget;
      const line = link.querySelector(".line");

      setHoveredFirm(null);

      gsap.to(link, {
        x: 0,
        color: "#000000",
        duration: 0.3,
        ease: "power2.out",
      });

      gsap.to(line, {
        width: 0,
        duration: 0.3,
        ease: "power2.out",
      });
    }
  };

  const handleFirmClick = (firm) => {
    // On mobile, toggle the selected firm
    if (window.innerWidth < 1024) {
      setSelectedFirm(selectedFirm === firm ? null : firm);
    } else {
      // On desktop, navigate to the firm's page using React Router
      navigate(firmLinks[firm]);
    }
  };

  const handleLearnMoreClick = (e, firm) => {
    e.stopPropagation(); // Prevent triggering the parent's click event
    window.open(firmLinks[firm]);
  };

  return (
    <div className="">
      {/* hero section */}
      <div className="relative w-full h-[60vh] lg:h-screen overflow-hidden">
        {/* Background Images */}
        <div
          ref={heroParallaxRef}
          className="absolute inset-0 w-full h-[130%] -top-[15%]"
        >
          {slides.map((slide, index) => (
            <div
              key={index}
              ref={(el) => (slideRefs.current[index] = el)}
              className="absolute inset-0 w-full h-full"
              style={{
                backgroundImage: `url(${slide})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                opacity: index === currentSlide ? 1 : 0,
              }}
            />
          ))}
        </div>

        {/* Dark overlay */}
        <div ref={overlayRef} className="absolute inset-0 bg-black/50" />

        {/* Content */}
        <div className="relative z-10 w-full h-full flex flex-col justify-end pb-8 sm:pb-12 md:pb-16 lg:pb-24 px-4 sm:px-8 md:px-16 lg:px-24">
          <div className="flex flex-col lg:flex-row items-end justify-between w-full gap-6 lg:gap-8">
            <div className="max-w-4xl">
              <h1
                ref={headerRef}
                className="text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-medium text-white mb-4 sm:mb-6"
              >
                Top 8 Architecture Firms In the world
              </h1>
              <p
                ref={textRef}
                className="text-base sm:text-lg text-white font-serif max-w-3xl font-light leading-relaxed"
              >
                Discover the world's leading architecture firms shaping
                skylines, cities, and future-forward spaces. These firms set
                global standards in innovation, sustainability, and design
                excellence.
              </p>
            </div>
            <div ref={navigationRef} className="flex items-end gap-4">
              {slides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => animateSlideChange(index)}
                  className={`h-[4px] transition-all duration-300 ${
                    currentSlide === index
                      ? "w-12 sm:w-16 bg-white"
                      : "w-8 sm:w-12 bg-white/50"
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* title section */}
      <div className="relative w-full lg:h-screen overflow-hidden bg-white py-16 sm:py-20 md:py-24 lg:py-0">
        <div className="relative z-10 w-full h-full flex flex-col items-center justify-center max-w-6xl mx-auto px-4 sm:px-8">
          <div className="flex items-center justify-center">
            <img
              ref={titleLogoRef}
              src={Logo}
              alt="Wonderful Eight"
              className="h-6 sm:h-8 md:h-12 mb-6 sm:mb-8"
            />
          </div>
          <h1
            ref={titleHeadingRef}
            className="text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-medium text-black max-w-3xl text-center mb-4 sm:mb-6"
          >
            Top 8 Architecture Firms In the world
          </h1>
          <p
            ref={titleTextRef}
            className="text-base sm:text-lg text-gray-600 max-w-2xl text-center font-light font-serif px-4"
          >
            From cultural landmarks to sustainable skyscrapers, these
            architecture firms are pushing the boundaries of design and shaping
            the cities of tomorrow.
          </p>
        </div>
      </div>

      {/* expertise section */}
      <div className="relative w-full min-h-[600px] lg:min-h-screen bg-white py-12 sm:py-16 md:py-20 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">
          {/* Left side - List */}
          <div className="flex flex-col px-4 sm:px-8 md:px-16 lg:pl-24 lg:pr-8">
            <div className="flex items-center gap-4 mb-8 sm:mb-12">
              <h2
                ref={expertiseTitleRef}
                className="text-sky-400 text-base sm:text-lg tracking-wide"
              >
                EXPERTISE
              </h2>
              <div
                ref={expertiseLineRef}
                className="h-[1px] w-20 sm:w-32 bg-sky-400"
              ></div>
            </div>
            <div className="">
              {[
                "Gensler",
                "Arcadis",
                "HDR (Henningson, Durham & Richardson)",
                "Nikken Sekkei",
                "AECOM",
                "Perkins + Will",
                "Skidmore, Owings & Merrill (SOM)",
                "Foster + Partners",
              ].map((firm, index) => (
                <div
                  key={index}
                  ref={(el) => (firmRefs.current[index] = el)}
                  className="border-b border-gray-200"
                >
                  <div
                    className="relative cursor-pointer py-4 sm:py-6"
                    onMouseEnter={(e) => handleFirmHoverEnter(e, firm)}
                    onMouseLeave={handleFirmHoverLeave}
                    onClick={() => handleFirmClick(firm)}
                  >
                    <h3 className="text-xl sm:text-2xl font-light">{firm}</h3>
                    <div className="line absolute bottom-0 left-0 w-0 bg-sky-400 hidden lg:block" />
                  </div>

                  {/* Mobile Image and Learn More Button */}
                  <div
                    className={`lg:hidden mt-4 transition-all duration-300 overflow-hidden ${
                      selectedFirm === firm
                        ? "max-h-[380px] opacity-100"
                        : "max-h-0 opacity-0"
                    }`}
                  >
                    <div className="flex flex-col gap-4">
                      <img
                        src={firmImages[firm]}
                        alt={firm}
                        className="w-full h-[300px] object-cover"
                      />
                      <button
                        onClick={(e) => handleLearnMoreClick(e, firm)}
                        className="flex items-center gap-2 text-sm font-medium border border-black px-4 py-2 hover:bg-black hover:text-white transition-colors"
                      >
                        Learn More
                        <svg
                          className="w-4 h-4"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M5 12H19M19 12L12 5M19 12L12 19"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right side - Image (Desktop only) */}
          <div className="relative h-full mt-8 lg:mt-0 hidden lg:block">
            <div className="sticky top-8 h-[800px] overflow-hidden">
              <div className="w-full h-full relative">
                <img
                  ref={expertiseImageRef}
                  src={hoveredFirm ? firmImages[hoveredFirm] : expertiseImage}
                  alt={hoveredFirm || "Modern office interior"}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* projects section */}
      <div className="relative w-full bg-cover bg-center min-h-[500px] lg:h-screen overflow-hidden">
        <div
          ref={projectsParallaxRef}
          className="absolute inset-0 w-full h-[130%] -top-[15%]"
        >
          <div
            className="w-full h-full"
            style={{
              backgroundImage: `url(${home4})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              transform: "scale(1.1)",
            }}
          />
        </div>
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative z-10 w-full h-full flex flex-col justify-center px-4 sm:px-8 md:px-16 lg:px-24 py-16 sm:py-20 md:py-24">
          <div className="max-w-3xl">
            <div className="flex items-center gap-4 mb-8 sm:mb-12">
              <h2
                ref={expertiseTitleRef}
                className="text-white text-base sm:text-lg tracking-wide"
              >
                PROJECTS
              </h2>
              <div
                ref={expertiseLineRef}
                className="h-[1px] w-20 sm:w-32 bg-white"
              ></div>
            </div>
            <h2
              ref={projectsTitleRef}
              className="text-white text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-medium mb-6 sm:mb-8"
            >
              Shaping Spaces That Inspire
            </h2>
            <p
              ref={projectsDescRef}
              className="text-white/90 text-base sm:text-lg md:text-xl font-light mb-8 sm:mb-12 max-w-2xl font-serif"
            >
              Take a closer look at the visionary spaces we've brought to life.
              Each project is a statement of creativity, precision, and a
              commitment to shaping environments that elevate the human
              experience.
            </p>
            <Link
              to="/expertise"
              ref={projectsButtonRef}
              className="inline-flex items-center text-white border border-white px-6 sm:px-8 py-2.5 sm:py-3 hover:bg-white hover:text-black transition-colors duration-300"
            >
              Learn More
              <svg
                className="ml-2 w-4 sm:w-5 h-4 sm:h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 8l4 4m0 0l-4 4m4-4H3"
                />
              </svg>
            </Link>
          </div>
        </div>
      </div>

      {/* related expertise section */}
      <div className="relative w-full min-h-[600px] overflow-hidden">
        <div
          ref={relatedExpertiseParallaxRef}
          className="absolute inset-0 w-full h-[130%] -top-[15%]"
        >
          <div
            className="w-full h-full"
            style={{
              backgroundImage: `url(${home7})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              transform: "scale(1.1)",
            }}
          />
        </div>
        <div className="relative z-10 w-full px-4 sm:px-8 md:px-16 lg:px-24 py-16 sm:py-20">
          <div className="ml-auto max-w-[600px] bg-white p-6 sm:p-8 md:p-12 lg:p-16 border-sky-400 border-b-[16px] sm:border-b-[24px] md:border-b-[32px]">
            <div className="flex items-center gap-4 mb-6 sm:mb-8">
              <h3
                ref={relatedExpertiseTitleRef}
                className="text-sky-400 text-sm sm:text-base uppercase tracking-[0.2em] font-medium"
              >
                RELATED EXPERTISE
              </h3>
              <div className="h-[1px] w-20 sm:w-32 bg-sky-400"></div>
            </div>

            <h2
              ref={relatedExpertiseHeadingRef}
              className="text-gray-900 text-2xl sm:text-3xl md:text-4xl font-medium mb-4 sm:mb-6 leading-tight"
            >
              Crafting the Spatial Experience from the Inside Out
            </h2>

            <p
              ref={relatedExpertiseTextRef}
              className="text-gray-600 text-base sm:text-lg font-light leading-relaxed mb-6 sm:mb-8 md:mb-10 font-serif"
            >
              it's about creating spaces that feel natural, intuitive, and
              inspiring. Every element, from layout and lighting to materials
              and movement, is carefully considered to ensure that beauty and
              practicality work hand in hand
            </p>

            <Link
              to="/expertise"
              ref={relatedExpertiseButtonRef}
              className="inline-flex items-center text-black border border-black px-6 sm:px-8 py-2.5 sm:py-3 hover:bg-black hover:text-white transition-colors duration-300 text-sm tracking-wider font-medium"
            >
              Learn More
            </Link>
          </div>
        </div>
      </div>

      {/* Featured Work Section */}
      <div className="w-full px-4 sm:px-8 md:px-16 lg:px-24 py-16 sm:py-20 md:py-24">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8 sm:mb-12 md:mb-16">
            <div className="flex items-center gap-4 mb-6 sm:mb-8">
              <h3 className="text-sky-400 text-sm sm:text-base uppercase tracking-[0.2em] font-medium">
                FEATURED WORK
              </h3>
              <div className="h-[1px] w-20 sm:w-32 bg-sky-400"></div>
            </div>

            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-[44px] font-medium mb-4 sm:mb-6 leading-tight">
              Masterpieces in Motion
            </h2>

            <p className="text-gray-600 text-base sm:text-lg font-light leading-relaxed max-w-2xl font-serif">
              Step into a world where architecture breathes life and energy.
              These featured works capture dynamic forms and visionary ideas
              that challenge conventions and inspire awe.
            </p>
          </div>

          {/* Image Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            {/* Image 1 - Curved Building */}
            <div className="aspect-[4/3] relative overflow-hidden group cursor-pointer">
              <div
                ref={(el) => (workImageRefs.current[0] = el)}
                className="absolute inset-0 w-full h-[120%] -top-[10%]"
              >
                <img
                  src={home4}
                  alt="Modern curved architectural building with glass facade"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>

            {/* Image 2 - Circular Stadium */}
            <div className="aspect-[4/3] relative overflow-hidden group cursor-pointer">
              <div
                ref={(el) => (workImageRefs.current[1] = el)}
                className="absolute inset-0 w-full h-[120%] -top-[10%]"
              >
                <img
                  src={home5}
                  alt="Modern circular stadium with vertical elements"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>

            {/* Image 3 - Airport Terminal */}
            <div className="aspect-[4/3] relative overflow-hidden group cursor-pointer">
              <div
                ref={(el) => (workImageRefs.current[2] = el)}
                className="absolute inset-0 w-full h-[120%] -top-[10%]"
              >
                <img
                  src={home6}
                  alt="Modern airport terminal with circular design"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>

            {/* Image 4 - Urban Planning */}
            <div className="aspect-[4/3] relative overflow-hidden group cursor-pointer">
              <div
                ref={(el) => (workImageRefs.current[3] = el)}
                className="absolute inset-0 w-full h-[120%] -top-[10%]"
              >
                <img
                  src={home8}
                  alt="Aerial view of urban planning project"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>
          </div>
        </div>
      </div>

      {/* News Section */}
      <div
        ref={newsRef}
        className="w-full px-4 sm:px-8 md:px-16 lg:px-24 py-16 sm:py-20 md:py-24 bg-white"
      >
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8 sm:mb-12">
            <div className="flex items-center gap-4 mb-6 sm:mb-8">
              <h3 className="text-sky-400 text-sm sm:text-base uppercase tracking-[0.2em] font-medium">
                NEWS
              </h3>
              <div className="h-[1px] w-20 sm:w-32 bg-sky-400"></div>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-medium">
              News & Insights
            </h2>
          </div>

          {/* Blog Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-6 sm:gap-8 mb-8">
            {blogPosts.slice(0, 3).map((post, index) => (
              <Link
                to={`/blog/${post.slug}`}
                key={post.id}
                ref={(el) => (newsCardRefs.current[index] = el)}
                className="group"
              >
                <div className="aspect-[4/3] overflow-hidden mb-4 sm:mb-6">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover bg-center bg-no-repeat"
                  />
                </div>
                <div className="space-y-2 sm:space-y-3">
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-gray-500">{post.category}</span>
                    <span className="text-gray-500">{post.date}</span>
                  </div>
                  <h3 className="text-lg sm:text-xl font-medium group-hover:text-sky-400 transition-colors">
                    {post.title}
                  </h3>
                </div>
              </Link>
            ))}
          </div>

          {/* See More Button */}
          <div className="flex justify-end">
            <Link
              to="/blog"
              className="inline-flex items-center text-black border border-black px-6 sm:px-8 py-2.5 sm:py-3 hover:bg-black hover:text-white transition-colors duration-300 text-sm tracking-wider font-medium"
            >
              See More
              <svg
                className="ml-2 w-4 sm:w-5 h-4 sm:h-5"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M13.5 19L20.5 12L13.5 5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M20.5 12L3.5 12"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
